import React, { useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import { toast } from "sonner";

export function LostCardsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recovering, setRecovering] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/lost-cards");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not scan Stripe");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);

  const recover = async (sid, cid) => {
    setRecovering(sid);
    try {
      await api.post("/admin/lost-cards/recover", { session_id: sid });
      toast.success(`Recovered card ${cid.slice(0, 8)}…`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recovery failed");
    } finally {
      setRecovering(null);
    }
  };

  if (!data && !busy) return null;
  const orphans = data?.orphans || [];

  return (
    <div className="mt-5 rounded-xl border border-[#E5E0D8] bg-white p-5" data-testid="lost-cards-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="serif text-lg text-[#112318]">Lost cards (paid Stripe sessions, no DB record)</p>
          <p className="text-xs text-[#4A5C50] mt-1 leading-relaxed max-w-xl">
            Sweeps the last 100 Stripe sessions for any paid checkout — cash-request,
            topup, or issuance — whose record is missing from the database. Typically
            caused by a missed webhook or DB wipe. Click <span className="mono">Recover</span> for
            cash-request orphans (rebuilds the card row) or topup/issuance orphans
            (creates the payment_transaction and triggers reconciliation). Either way,
            user gets credited within seconds.
          </p>
        </div>
        <button type="button" onClick={load} disabled={busy}
          className="px-3 py-1.5 rounded-lg border border-[#184A30]/30 text-[#184A30] text-xs hover:bg-[#184A30]/5 disabled:opacity-50"
          data-testid="lost-cards-refresh">
          {busy ? "Scanning…" : "Refresh"}
        </button>
      </div>
      {data && (
        <p className="text-[11px] mono text-[#4A5C50] mt-2" data-testid="lost-cards-summary">
          Scanned {data.scanned} Stripe sessions · {orphans.length} lost card{orphans.length === 1 ? "" : "s"}
        </p>
      )}
      {orphans.length > 0 && (
        <ul className="mt-4 divide-y divide-[#E5E0D8] border border-[#E5E0D8] rounded-xl overflow-hidden">
          {orphans.map((o) => {
            const isCash = (o.kind || "cash_request") === "cash_request";
            const idLabel = o.card_id ? o.card_id.slice(0, 8) : o.session_id.slice(0, 12);
            return (
              <li key={o.session_id} className="flex items-center gap-3 p-3" data-testid={`lost-card-${idLabel}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#112318] flex items-center gap-2">
                    <span className="mono">{idLabel}</span>
                    <span className={`text-[10px] mono px-1.5 py-0.5 rounded ${
                      isCash ? "bg-[#184A30]/10 text-[#184A30]" : "bg-[#CBA35C]/15 text-[#8C6B21]"
                    }`}>
                      {o.kind || "unknown"}
                    </span>
                    <span className="text-[#4A5C50]">· {o.phone || o.email || "(no contact)"}</span>
                  </p>
                  <p className="text-[11px] mono text-[#4A5C50] mt-0.5 truncate">
                    ${Number(o.amount_usd).toFixed(2)} · {o.session_id.slice(0, 28)}… · {new Date(o.stripe_created * 1000).toLocaleString()}
                  </p>
                </div>
                <button type="button" onClick={() => recover(o.session_id, o.card_id || o.session_id)}
                  disabled={recovering === o.session_id}
                  className="text-xs px-3 py-1.5 rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-white disabled:opacity-50"
                  data-testid={`lost-card-recover-${idLabel}`}>
                  {recovering === o.session_id ? "Recovering…" : "Recover"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {data && orphans.length === 0 && (
        <p className="text-xs text-[#184A30] mt-3" data-testid="lost-cards-clean">
          ✓ Every paid Stripe session has a matching card record. No lost cards.
        </p>
      )}
    </div>
  );
}
